// Qibla direction (great-circle bearing to the Kaaba) and distance helpers.
// Reference: Mecca / Kaaba coordinates (Masjid al-Haram).

import { BD_CITIES, type BDCity } from "@/data/bdCities";

export const KAABA_LAT = 21.4225;
export const KAABA_LON = 39.8262;
export const EARTH_RADIUS_KM = 6371.0088;

const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;

/** Initial great-circle bearing (true north, clockwise) from (lat,lon) toward the Kaaba. */
export function qiblaBearing(lat: number, lon: number): number {
  const phi1 = toRad(lat);
  const phi2 = toRad(KAABA_LAT);
  const dLon = toRad(KAABA_LON - lon);
  const y = Math.sin(dLon) * Math.cos(phi2);
  const x =
    Math.cos(phi1) * Math.sin(phi2) -
    Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLon);
  const brng = Math.atan2(y, x);
  return (toDeg(brng) + 360) % 360;
}

/** Great-circle (haversine) distance in kilometres to the Kaaba. */
export function distanceToKaabaKm(lat: number, lon: number): number {
  const phi1 = toRad(lat);
  const phi2 = toRad(KAABA_LAT);
  const dPhi = toRad(KAABA_LAT - lat);
  const dLon = toRad(KAABA_LON - lon);
  const a =
    Math.sin(dPhi / 2) ** 2 +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c;
}

export interface CardinalLabel {
  en: string;
  bn: string;
  short: { en: string; bn: string };
}

/** Map a bearing (0-360°) to a 16-point cardinal description. */
export function cardinalFromBearing(bearing: number): CardinalLabel {
  const b = ((bearing % 360) + 360) % 360;
  const points: CardinalLabel[] = [
    { en: "North", bn: "উত্তর", short: { en: "N", bn: "উ" } },
    { en: "North-northeast", bn: "উত্তর-উত্তর-পূর্ব", short: { en: "NNE", bn: "উউপূ" } },
    { en: "Northeast", bn: "উত্তর-পূর্ব", short: { en: "NE", bn: "উপূ" } },
    { en: "East-northeast", bn: "পূর্ব-উত্তর-পূর্ব", short: { en: "ENE", bn: "পূউপূ" } },
    { en: "East", bn: "পূর্ব", short: { en: "E", bn: "পূ" } },
    { en: "East-southeast", bn: "পূর্ব-দক্ষিণ-পূর্ব", short: { en: "ESE", bn: "পূদপূ" } },
    { en: "Southeast", bn: "দক্ষিণ-পূর্ব", short: { en: "SE", bn: "দপূ" } },
    { en: "South-southeast", bn: "দক্ষিণ-দক্ষিণ-পূর্ব", short: { en: "SSE", bn: "দদপূ" } },
    { en: "South", bn: "দক্ষিণ", short: { en: "S", bn: "দ" } },
    { en: "South-southwest", bn: "দক্ষিণ-দক্ষিণ-পশ্চিম", short: { en: "SSW", bn: "দদপ" } },
    { en: "Southwest", bn: "দক্ষিণ-পশ্চিম", short: { en: "SW", bn: "দপ" } },
    { en: "West-southwest", bn: "পশ্চিম-দক্ষিণ-পশ্চিম", short: { en: "WSW", bn: "পদপ" } },
    { en: "West", bn: "পশ্চিম", short: { en: "W", bn: "প" } },
    { en: "West-northwest", bn: "পশ্চিম-উত্তর-পশ্চিম", short: { en: "WNW", bn: "পউপ" } },
    { en: "Northwest", bn: "উত্তর-পশ্চিম", short: { en: "NW", bn: "উপ" } },
    { en: "North-northwest", bn: "উত্তর-উত্তর-পশ্চিম", short: { en: "NNW", bn: "উউপ" } },
  ];
  const idx = Math.round(b / 22.5) % 16;
  return points[idx];
}

export interface QiblaResult {
  bearing: number;
  distanceKm: number;
  cardinal: CardinalLabel;
}

export function qiblaFor(lat: number, lon: number): QiblaResult {
  const bearing = qiblaBearing(lat, lon);
  return {
    bearing,
    distanceKm: distanceToKaabaKm(lat, lon),
    cardinal: cardinalFromBearing(bearing),
  };
}

export interface CityQibla {
  city: BDCity;
  bearing: number;
  distanceKm: number;
}

/** Precomputed Qibla bearings & distances for all 64 BD districts (sorted by city name). */
export const BD_CITY_QIBLA: CityQibla[] = BD_CITIES.map((city) => ({
  city,
  bearing: qiblaBearing(city.lat, city.lon),
  distanceKm: distanceToKaabaKm(city.lat, city.lon),
})).sort((a, b) => a.city.en.localeCompare(b.city.en));
